import React, { useState } from 'react';
import PropTypes from 'prop-types';
import './SolutionsModal.css';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faCircleNodes,
  faIdCardClip,
  faBullseye,
  faMeteor,
  faInfoCircle,
  faNewspaper,
} from '@fortawesome/free-solid-svg-icons';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';

// Traducciones completas del modal
const modalTranslations2 = {
  es: {
    header: 'Un FrameWork digitalizado que te ayuda',
    intro:
      'Identifica al instante dónde tu equipo puede crecer y cómo responder mejor a los retos clave de tu organización. Evalúa su idoneidad y alinéalo con las oportunidades estratégicas.',
    sections: [
      {
        icon: faCircleNodes,
        title: 'Evaluación Integral de Competencias',
        bullets: [
          '24 competencias clave: desde liderazgo y comunicación hasta innovación y adaptabilidad.',
          'Puntuación automática en cada habilidad, contrastada con benchmarks internos y del sector.',
          'Recursos de desarrollo digitales asociados a cada competencia (cursos, microlearning, artículos).',
        ],
      },
      {
        icon: faIdCardClip,
        title: 'Biblioteca de Perfiles de Rol',
        bullets: [
          '+100 perfiles preconfigurados para puestos comunes (ventas, finanzas, IT, operaciones, etc.).',
          'Definición clara de objetivos, responsabilidades y habilidades esenciales.',
          'Facilita la comparación entre el talento actual y los requisitos del puesto.',
        ],
      },
      {
        icon: faBullseye,
        title: 'TAP® Framework: Desarrollo Estratégico',
        bullets: [
          'Consejos personalizados según el “valor estratégico” de cada empleado:',
          'Core Performers: estabilizar y optimizar.',
          'High Potentials: retar y acelerar.',
          'Critical Contributors: retener y reconocer.',
          'Planes de acción con roadmaps de aprendizaje, mentoring y evaluaciones periódicas.',
        ],
      },
    ],
    benefitsText:
      'Mira todos los beneficios que puedes obtener al poner en práctica una estrategia como esta:',
    sourceLabel: 'Fuente',
    kpis: [
      {
        metric: 'Role Fit Score',
        baseline: 73,
        improved: 85,
        unit: '%',
        center: '+16%',
        tooltipText:
          'Nuestro módulo de BetherFramework® ajusta en tiempo real los perfiles de competencias frente a los requisitos del rol (incluyendo +100 perfiles predefinidos), lo que aumenta la claridad de expectativas y la precisión de los “matchings”. Esto se traduce en un salto de 73 → 85 en la puntuación media de idoneidad.',
        link: 'https://www.oracle.com/a/ocom/docs/gated/deloitte-people-adaptability-and-resilience.pdf',
      },
      {
        metric: 'Internal Mobility Rate',
        baseline: 15,
        improved: 30,
        unit: '%',
        center: '×2',
        tooltipText:
          'Con la biblioteca de roles y la evaluación de potencial TAP®, Bether identifica talento interno oculto y muestra rutas de carrera personalizadas. La visibilidad de oportunidades y planes claros de sucesión doblan la tasa de absorción interna, pasando de un 15 % a un 30 % de vacantes cubiertas con talento propio.',
        link: 'https://www.reworked.co/learning-development/lateral-moves-help-employees-climb-the-corporate-ladder/',
      },
      {
        metric: 'Satisfacción con el Plan de Desarrollo',
        baseline: 48.3,
        improved: 85,
        unit: '%',
        center: '+36%',
        tooltipText:
          'Satisfacción con el Plan de Desarrollo (+76 % relativa): Al combinar feedback continuo, roadmaps digitales y recursos de desarrollo 100 % online, Bether convierte los planes de formación en experiencias dinámicas y medibles. Pasar de un 48.3 % de satisfacción a un 85 % refleja el valor percibido de itinerarios claros, contenidos a medida y seguimiento automatizado.',
        link: 'https://www.conference-board.org/press/job-satisfaction-hits-all-time-high',
      },
    ],
  },
  en: {
    header: 'A Digitized Framework That Helps You',
    intro:
      "Instantly identify where your team can grow and how to best respond to your organization's key challenges. Assess their fit and align them with strategic opportunities.",
    sections: [
      {
        icon: faCircleNodes,
        title: 'Comprehensive Competency Assessment',
        bullets: [
          '24 key competencies: from leadership and communication to innovation and adaptability.',
          'Automatic scoring on each skill, benchmarked against internal and industry standards.',
          'Digital development resources linked to each competency (courses, microlearning, articles).',
        ],
      },
      {
        icon: faIdCardClip,
        title: 'Role Profile Library',
        bullets: [
          '100+ preconfigured profiles for common roles (sales, finance, IT, operations, etc.).',
          'Clear definition of objectives, responsibilities, and essential skills.',
          'Easily compare current talent against role requirements.',
        ],
      },
      {
        icon: faBullseye,
        title: 'TAP® Framework: Strategic Development',
        bullets: [
          'Personalized guidance based on each employee’s “strategic value”:',
          'Core Performers: stabilize and optimize.',
          'High Potentials: challenge and accelerate.',
          'Critical Contributors: retain and recognize.',
          'Action plans with learning roadmaps, mentoring, and periodic evaluations.',
        ],
      },
    ],
    benefitsText:
      'See all the benefits you can gain by implementing a strategy like this:',
    sourceLabel: 'Source',
    kpis: [
      {
        metric: 'Role Fit Score',
        baseline: 73,
        improved: 85,
        unit: '%',
        center: '+16 %',
        tooltipText:
          'Role Fit Score (+16 %): Our BetherFramework® module dynamically adjusts competency profiles against role requirements (including 100+ predefined profiles), enhancing expectation clarity and matching accuracy. This drives a jump from 73 → 85 in average fit score.',
        link: 'https://www.oracle.com/a/ocom/docs/gated/deloitte-people-adaptability-and-resilience.pdf',
      },
      {
        metric: 'Internal Mobility Rate',
        baseline: 15,
        improved: 30,
        unit: '%',
        center: '×2',
        tooltipText:
          'Internal Mobility Rate (×2): With the role library and TAP® potential assessment, Bether uncovers hidden internal talent and shows personalized career paths. Visibility and clear succession plans double the internal fill rate from 15% to 30%.',
        link: 'https://www.reworked.co/learning-development/lateral-moves-help-employees-climb-the-corporate-ladder/',
      },
      {
        metric: 'Development Plan Satisfaction',
        baseline: 48.3,
        improved: 85,
        unit: '%',
        center: '+36%',
        tooltipText:
          'Development Plan Satisfaction (+76% relative): By combining continuous feedback, digital roadmaps, and fully online development resources, Bether transforms training plans into dynamic, measurable experiences. Satisfaction rises from 48.3% to 85%.',
        link: 'https://www.conference-board.org/press/job-satisfaction-hits-all-time-high',
      },
    ],
  },
};

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const entry = payload[0];
    return (
      <div className="tooltip-box">
        <strong>{entry.name}</strong>: {entry.value.toFixed(1)}
        {entry.payload.unit}
      </div>
    );
  }
  return null;
};

const KPICharts = ({ kpis, sourceLabel }) => {
  const [infoVisible, setInfoVisible] = useState({});
  const toggleInfo = (metric) =>
    setInfoVisible((prev) => ({ ...prev, [metric]: !prev[metric] }));

  return (
    <div className="smodal-tei-charts">
      {kpis.map(
        ({ metric, baseline, improved, unit, center, tooltipText, link }) => {
          const outerData = [
            { name: 'Bether Average', value: improved, unit },
            { name: '', value: 100 - improved },
          ];
          const innerData = [
            { name: 'Business Average', value: baseline, unit },
            { name: '', value: 100 - baseline },
          ];
          return (
            <div key={metric} className="smodal-tei-chart-card">
              <h4 className="smodal-tei-chart-title">{metric}</h4>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={outerData}
                    dataKey="value"
                    nameKey="name"
                    startAngle={90}
                    endAngle={-270}
                    innerRadius="65%"
                    outerRadius="95%"
                    paddingAngle={2}
                    isAnimationActive={false}
                  >
                    {outerData.map((_, idx) => (
                      <Cell
                        key={idx}
                        fill={idx === 0 ? '#ff2300' : '#e0e0e0'}
                      />
                    ))}
                  </Pie>
                  <Pie
                    data={innerData}
                    dataKey="value"
                    nameKey="name"
                    startAngle={90}
                    endAngle={-270}
                    innerRadius="45%"
                    outerRadius="63%"
                    paddingAngle={2}
                    isAnimationActive={false}
                  >
                    {innerData.map((_, idx) => (
                      <Cell
                        key={idx}
                        fill={idx === 0 ? '#555555' : '#e0e0e0'}
                      />
                    ))}
                  </Pie>
                  <text
                    x="50%"
                    y="50%"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    className="smodal-tei-chart-center"
                  >
                    {center}
                  </text>
                  <RechartsTooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <FontAwesomeIcon
                icon={faInfoCircle}
                className="info-icon"
                onClick={() => toggleInfo(metric)}
              />
              {infoVisible[metric] && (
                <div className="tooltip-popup">
                  <div className="tooltip-text">{tooltipText}</div>
                  <button
                    className="source-button"
                    onClick={() => window.open(link, '_blank')}
                  >
                    <FontAwesomeIcon icon={faNewspaper} />
                    &nbsp;{sourceLabel}
                  </button>
                </div>
              )}
            </div>
          );
        }
      )}
    </div>
  );
};

const SolutionsModal2 = ({ isOpen, onClose, language = 'es' }) => {
  if (!isOpen) return null;
  const t = modalTranslations2[language];

  return (
    <div className="smodal-overlay">
      <div className="smodal-content">
        <button className="smodal-close" onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} />
        </button>
        <h2 className="smodal-header">{t.header}</h2>
        <p className="smodal-tei-intro">{t.intro}</p>

        {t.sections.map((sec, idx) => (
          <div key={idx} className="smodal-section">
            <FontAwesomeIcon icon={sec.icon} className="section-icon" />
            <div className="section-content">
              <h3 className="section-title">{sec.title}</h3>
              <ul className="section-list">
                {sec.bullets.map((bullet, bidx) => (
                  <li key={bidx}>{bullet}</li>
                ))}
              </ul>
            </div>
          </div>
        ))}

        <div className="smodal-tei-box">
          <h3 className="smodal-tei-title">
            <FontAwesomeIcon icon={faMeteor} className="tei-icon" /> TEI: Total
            Economic Impact
          </h3>
          <p className="smodal-tei-intro">{t.benefitsText}</p>
          <KPICharts kpis={t.kpis} sourceLabel={t.sourceLabel} />
        </div>
      </div>
    </div>
  );
};

SolutionsModal2.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  language: PropTypes.oneOf(['es', 'en']),
};

export default SolutionsModal2;
